import type {
  CreateOrgInvitationRequest,
  CreateOrgInvitationResponse,
  ListOrgInvitationsResponse,
  ListOrgMembersResponse,
  RevokeOrgInvitationResponse,
} from '@nadoutong/contracts';
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import type {
  UseMutationResult,
  UseQueryResult,
} from '@tanstack/react-query';
import { useSession } from '../hooks/useSession.tsx';
import {
  createOrgInvitation,
  listOrgInvitations,
  listOrgMembers,
  revokeOrgInvitation,
} from './orgs.ts';

/**
 * React Query wrappers for the `/v1/orgs/*` surface (members + invitation
 * lifecycle). Each key includes the `orgId` so callers with multi-org
 * sessions in the future get independent caches.
 *
 * Invalidation map:
 *   - createOrgInvitation → invalidate invitation list for the org
 *   - revokeOrgInvitation → invalidate invitation list for the org
 */

export const ORG_MEMBERS_KEY = (orgId: string) => ['orgs', orgId, 'members'] as const;
export const ORG_INVITATIONS_KEY = (orgId: string) =>
  ['orgs', orgId, 'invitations'] as const;

export function useOrgMembers(
  orgId: string | undefined,
): UseQueryResult<ListOrgMembersResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: orgId === undefined ? ['orgs', 'missing', 'members'] : ORG_MEMBERS_KEY(orgId),
    queryFn: ({ signal }) => {
      if (orgId === undefined) {
        return Promise.reject(new Error('orgId is required'));
      }
      return listOrgMembers(orgId, fetchImpl, signal);
    },
    enabled: orgId !== undefined,
  });
}

export function useOrgInvitations(
  orgId: string | undefined,
): UseQueryResult<ListOrgInvitationsResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey:
      orgId === undefined ? ['orgs', 'missing', 'invitations'] : ORG_INVITATIONS_KEY(orgId),
    queryFn: ({ signal }) => {
      if (orgId === undefined) {
        return Promise.reject(new Error('orgId is required'));
      }
      return listOrgInvitations(orgId, fetchImpl, signal);
    },
    enabled: orgId !== undefined,
  });
}

export interface CreateInvitationVars {
  readonly orgId: string;
  readonly body: CreateOrgInvitationRequest;
}

export function useCreateOrgInvitation(): UseMutationResult<
  CreateOrgInvitationResponse,
  Error,
  CreateInvitationVars
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ orgId, body }) => createOrgInvitation(orgId, body, fetchImpl),
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({ queryKey: ORG_INVITATIONS_KEY(variables.orgId) });
    },
  });
}

export interface RevokeInvitationVars {
  readonly orgId: string;
  readonly invitationId: string;
}

export function useRevokeOrgInvitation(): UseMutationResult<
  RevokeOrgInvitationResponse,
  Error,
  RevokeInvitationVars
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ orgId, invitationId }) =>
      revokeOrgInvitation(orgId, invitationId, fetchImpl),
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({ queryKey: ORG_INVITATIONS_KEY(variables.orgId) });
    },
  });
}
